// User Guide Page - Comprehensive documentation for Magnus Core (DFMEA)
// Covers AI Assistants, AIAG-VDA workflow, and all app features
// ============================================================

import React, { useState } from 'react';
import { useAppContext } from '../contexts/AppContext';
import { Link } from 'react-router-dom';
import {
    BookOpen,
    Sparkles,
    Bot,
    ChevronRight,
    ChevronDown,
    AlertTriangle,
    CheckCircle,
    Clock,
    Target,
    Shield,
    GitBranch,
    FileSpreadsheet,
    ClipboardCheck,
    BarChart2,
    FileText,
    Zap,
    Search,
    ArrowRight,
    ArrowLeft,
    X,
    Activity,
    Users,
    TrendingUp,
    Award,
    Layers,
    Box
} from 'lucide-react';

interface GuideSection {
    id: string;
    title: string;
    icon: React.ReactNode;
    subsections?: { id: string; title: string }[];
}

const UserGuidePage: React.FC = () => {
    const { theme } = useAppContext();
    const isDark = theme === 'dark';
    const [activeSection, setActiveSection] = useState('overview');
    const [expandedSections, setExpandedSections] = useState<string[]>(['ai-assistants', 'workflow']);
    const [searchTerm, setSearchTerm] = useState('');

    const guideSections: GuideSection[] = [
        {
            id: 'overview',
            title: 'Getting Started',
            icon: <BookOpen size={18} />,
            subsections: [
                { id: 'overview-intro', title: 'Welcome to Magnus DFMEA' },
                { id: 'overview-benefits', title: 'Key Benefits' },
                { id: 'overview-quickstart', title: 'Quick Start Guide' },
            ]
        },
        {
            id: 'ai-assistants',
            title: 'AI Assistants',
            icon: <Bot size={18} />,
            subsections: [
                { id: 'ai-magnus', title: 'Magnus AI Assistant' },
                { id: 'ai-ideation', title: 'AI DFMEA Ideation' },
                { id: 'ai-agents', title: 'Specialized AI Agents' },
            ]
        },
        {
            id: 'workflow',
            title: 'AIAG-VDA 7-Step Workflow',
            icon: <Activity size={18} />,
            subsections: [
                { id: 'step1', title: 'Step 1: Planning & Preparation' },
                { id: 'step2', title: 'Step 2: Structure Analysis' },
                { id: 'step3', title: 'Step 3: Function Analysis' },
                { id: 'step4', title: 'Step 4: Failure Analysis' },
                { id: 'step5', title: 'Step 5: Risk Analysis' },
                { id: 'step6', title: 'Step 6: Optimization' },
                { id: 'step7', title: 'Step 7: Documentation' },
            ]
        },
        {
            id: 'features',
            title: 'App Features',
            icon: <Zap size={18} />,
            subsections: [
                { id: 'feature-dashboard', title: 'Dashboard & Metrics' },
                { id: 'feature-structure', title: 'Structure Tree & BoM' },
                { id: 'feature-pdiagram', title: 'P-Diagram' },
                { id: 'feature-dfmea', title: 'DFMEA Worksheet' },
                { id: 'feature-dvpr', title: 'DVP&R' },
                { id: 'feature-reports', title: 'Reports & Export' },
            ]
        },
    ];

    const toggleSection = (sectionId: string) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const scrollToSection = (sectionId: string) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar - Table of Contents */}
            <aside className={`
                w-72 fixed left-64 top-20 bottom-0 overflow-y-auto border-r p-4
                ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}
            `} style={{ left: '0', top: '64px', zIndex: 40 }}>
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h2 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            <BookOpen size={20} className="text-primary" />
                            User Guide
                        </h2>
                        <p className="text-sm text-slate-500 mt-1">Complete documentation</p>
                    </div>
                    <button
                        onClick={() => window.history.back()}
                        className={`p-2 rounded-lg transition-all ${isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                        title="Close Guide"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Back to App Link */}
                <Link
                    to="/dashboard"
                    className={`flex items-center gap-2 px-3 py-2 mb-4 rounded-lg text-sm font-semibold transition-all border ${isDark
                        ? 'bg-blue-900/20 border-blue-800 text-blue-400 hover:bg-blue-900/40'
                        : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'
                        }`}
                >
                    <ArrowLeft size={16} />
                    Back to Dashboard
                </Link>

                {/* Search */}
                <div className="relative mb-4">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search guide..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`
                            w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary
                            ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}
                        `}
                    />
                </div>

                {/* Navigation */}
                <nav className="space-y-1">
                    {guideSections.map(section => (
                        <div key={section.id}>
                            <button
                                onClick={() => {
                                    toggleSection(section.id);
                                    scrollToSection(section.id);
                                }}
                                className={`
                                    w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors
                                    ${activeSection === section.id || activeSection.startsWith(section.id)
                                        ? 'bg-primary text-white'
                                        : isDark
                                            ? 'text-slate-300 hover:bg-slate-800'
                                            : 'text-slate-700 hover:bg-slate-100'
                                    }
                                `}
                            >
                                <span className="flex items-center gap-2">
                                    {section.icon}
                                    {section.title}
                                </span>
                                {section.subsections && (
                                    expandedSections.includes(section.id)
                                        ? <ChevronDown size={16} />
                                        : <ChevronRight size={16} />
                                )}
                            </button>
                            {section.subsections && expandedSections.includes(section.id) && (
                                <div className="ml-6 mt-1 space-y-1">
                                    {section.subsections.map(sub => (
                                        <button
                                            key={sub.id}
                                            onClick={() => scrollToSection(sub.id)}
                                            className={`
                                                w-full text-left px-3 py-1.5 rounded text-xs transition-colors
                                                ${activeSection === sub.id
                                                    ? 'text-primary font-medium'
                                                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                                }
                                            `}
                                        >
                                            {sub.title}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 ml-72 p-8 space-y-12" style={{ marginTop: '64px' }}>
                {/* ============================================================ */}
                {/* SECTION: OVERVIEW */}
                {/* ============================================================ */}
                <section id="overview">
                    <div id="overview-intro" className={`p-8 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-800' : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'}`}>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                <Shield className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                    Welcome to Magnus DFMEA
                                </h1>
                                <p className="text-lg text-slate-500">AI-Powered Design Failure Mode and Effects Analysis</p>
                            </div>
                        </div>
                        <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                            Magnus DFMEA is a next-generation, AI-first platform that follows the <strong>AIAG-VDA 7-Step DFMEA methodology</strong>.
                            It's designed to help engineering teams identify, assess, and mitigate design risks securely and efficiently.
                        </p>
                        <div className="mt-6">
                            <Link
                                to="/learning-center"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                            >
                                <Zap size={18} />
                                Visit Premium Learning Center
                            </Link>
                        </div>
                    </div>

                    {/* Key Benefits */}
                    <div id="overview-benefits" className="mt-8">
                        <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            <TrendingUp className="text-green-500" />
                            Key Benefits
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { icon: <Clock size={24} />, title: 'Accelerated Design', desc: 'AI auto-generates failure modes and causes from design parameters', color: 'blue' },
                                { icon: <AlertTriangle size={24} />, title: 'Risk Reduction', desc: 'Systematic analysis ensures robust design verification', color: 'red' },
                                { icon: <CheckCircle size={24} />, title: 'Standards Compliant', desc: 'Built-in support for AIAG-VDA, ISO 26262, and more', color: 'green' },
                                { icon: <Users size={24} />, title: 'Collaboration', desc: 'Real-time multi-user editing and action tracking', color: 'purple' },
                                { icon: <Sparkles size={24} />, title: 'AI Assistants', desc: 'Specialized agents for Physics of Failure and Boundary Analysis', color: 'amber' },
                                { icon: <Target size={24} />, title: 'Knowledge Reuse', desc: ' leverage libraries of past failure modes and controls', color: 'cyan' },
                            ].map((benefit, idx) => (
                                <div
                                    key={idx}
                                    className={`
                                        p-5 rounded-xl border-l-4 transition-all hover:shadow-lg
                                        ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                                    `}
                                    style={{ borderLeftColor: benefit.color === 'blue' ? '#3B82F6' : benefit.color === 'red' ? '#EF4444' : benefit.color === 'green' ? '#10B981' : benefit.color === 'purple' ? '#8B5CF6' : benefit.color === 'amber' ? '#F59E0B' : '#06B6D4' }}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className={`p-2 rounded-lg ${isDark ? 'bg-slate-700' : 'bg-slate-100'}`}>
                                            {benefit.icon}
                                        </div>
                                        <h3 className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{benefit.title}</h3>
                                    </div>
                                    <p className="text-sm text-slate-500">{benefit.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Quick Start */}
                    <div id="overview-quickstart" className="mt-8">
                        <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            <Zap className="text-amber-500" />
                            Quick Start Guide
                        </h2>
                        <div className={`p-6 rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                            <ol className="space-y-4">
                                {[
                                    { step: 1, title: 'Select or Create a Project', desc: 'Start by selecting a project from the Dashboard', link: '/' },
                                    { step: 2, title: 'Define Structure (BoM)', desc: 'Build your system, subsystem, and component hierarchy', link: '/system-definition' },
                                    { step: 3, title: 'Create P-Diagram', desc: 'Analyze inputs, outputs, noise factors, and control factors', link: '/p-diagram' },
                                    { step: 4, title: 'Develop DFMEA', desc: 'Identify failure modes, effects, and causes in the worksheet', link: '/fmea-editor' },
                                    { step: 5, title: 'Plan & Track DVP&R', desc: 'Create verification plans linked to your failure modes', link: '/dvpr' },
                                ].map(item => (
                                    <li key={item.step} className="flex items-start gap-4">
                                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold flex-shrink-0">
                                            {item.step}
                                        </div>
                                        <div>
                                            <Link to={item.link} className="font-semibold text-primary hover:underline flex items-center gap-1">
                                                {item.title}
                                                <ArrowRight size={14} />
                                            </Link>
                                            <p className="text-sm text-slate-500">{item.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                </section>

                {/* ============================================================ */}
                {/* SECTION: AI ASSISTANTS */}
                {/* ============================================================ */}
                <section id="ai-assistants">
                    <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        <Bot className="text-purple-500" />
                        AI Assistants & Tools
                    </h2>
                    <p className={`text-lg mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        Magnus DFMEA leverages advanced AI to anticipate design failures using Physics of Failure principles and historical data.
                    </p>

                    {/* Magnus AI Assistant */}
                    <div id="ai-magnus" className={`p-6 rounded-xl border mb-6 ${isDark ? 'bg-gradient-to-r from-pink-900/30 to-blue-900/30 border-pink-800' : 'bg-gradient-to-r from-pink-50 to-blue-50 border-pink-200'}`}>
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center">
                                <Sparkles className="text-white" size={28} />
                            </div>
                            <div>
                                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Magnus AI Assistant</h3>
                                <p className="text-slate-500">Your co-pilot for reliability engineering</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-white/70'}`}>
                                <h4 className="font-semibold text-blue-600 mb-2">🚀 Guidance Mode</h4>
                                <ul className="text-sm text-slate-500 space-y-1">
                                    <li>• Navigational help</li>
                                    <li>• Explanations of DFMEA concepts</li>
                                    <li>• Standard references (AIAG, ISO)</li>
                                </ul>
                            </div>
                            <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-white/70'}`}>
                                <h4 className="font-semibold text-purple-600 mb-2">⚡ Analysis Mode</h4>
                                <ul className="text-sm text-slate-500 space-y-1">
                                    <li>• Suggests potential failure modes</li>
                                    <li>• Recommends controls based on Physics of Failure</li>
                                    <li>• Reviews S-O-D ratings for consistency</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Specialized AI Agents */}
                    <div id="ai-agents" className="space-y-4">
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                            Specialized AI Agents
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                {
                                    icon: '🔬',
                                    name: 'Physics of Failure Agent',
                                    focus: 'Failure Mechanism Analysis',
                                    color: 'red',
                                    helps: [
                                        'Identifies wear-out mechanisms',
                                        'Analyzes stress-strength interference',
                                        'Suggests accelerated life tests'
                                    ]
                                },
                                {
                                    icon: '🌐',
                                    name: 'Boundary Analysis Agent',
                                    focus: 'Interface & Interaction',
                                    color: 'blue',
                                    helps: [
                                        'Analyzes system interfaces (Physical, Energy, Info)',
                                        'Identifies noise factors',
                                        'Suggests robust design parameter settings'
                                    ]
                                },
                                {
                                    icon: '📜',
                                    name: 'Compliance Agent',
                                    focus: 'Standards & Requirements',
                                    color: 'green',
                                    helps: [
                                        'Checks against ISO 26262 / IATF 16949',
                                        'Verifies requirement traceability',
                                        'Audits for completeness'
                                    ]
                                },
                                {
                                    icon: '📈',
                                    name: 'DVP&R Agent',
                                    focus: 'Verification Planning',
                                    color: 'amber',
                                    helps: [
                                        'Suggests detection methods',
                                        'Calculates sample sizes',
                                        'Links tests to failure modes'
                                    ]
                                }
                            ].map((agent, idx) => (
                                <div
                                    key={idx}
                                    className={`
                                        p-5 rounded-xl border transition-all hover:shadow-lg
                                        ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                                    `}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-2xl">{agent.icon}</span>
                                        <div>
                                            <h4 className={`font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>{agent.name}</h4>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                                {agent.focus}
                                            </span>
                                        </div>
                                    </div>
                                    <ul className="space-y-1">
                                        {agent.helps.map((help, i) => (
                                            <li key={i} className="text-sm text-slate-500 flex items-start gap-2">
                                                <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                                                {help}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ============================================================ */}
                {/* SECTION: AIAG-VDA WORKFLOW */}
                {/* ============================================================ */}
                <section id="workflow">
                    <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        <Activity className="text-blue-500" />
                        AIAG-VDA 7-Step Workflow
                    </h2>
                    <p className={`text-lg mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                        Magnus DFMEA adheres to the AIAG-VDA 7-Step approach, guiding you from planning to documentation.
                    </p>

                    <div className="space-y-6">
                        {[
                            {
                                id: 'step1', num: 1, title: 'Planning & Preparation', icon: <Target size={20} />, color: 'blue',
                                apps: ['Project Scoping'],
                                description: 'Define project scope (5T), Identify baseline DFMEA, and set boundaries.',
                                delivers: ['Project Plan', 'Scope Definition']
                            },
                            {
                                id: 'step2', num: 2, title: 'Structure Analysis', icon: <Layers size={20} />, color: 'purple',
                                apps: ['Structure Tree', 'Block Diagram'],
                                description: 'Visualize the system, subsystem, and component hierarchy and interfaces.',
                                delivers: ['Structure Tree', 'Block Diagram']
                            },
                            {
                                id: 'step3', num: 3, title: 'Function Analysis', icon: <FileSpreadsheet size={20} />, color: 'green',
                                apps: ['P-Diagram', 'Function Matrix'],
                                description: 'Associate functions with structural elements and define requirements.',
                                delivers: ['Parameter Diagram (P-Diagram)', 'Function Net']
                            },
                            {
                                id: 'step4', num: 4, title: 'Failure Analysis', icon: <AlertTriangle size={20} />, color: 'red',
                                apps: ['DFMEA Worksheet'],
                                description: 'Identify Failure Modes, Effects, and Causes. Establish F-E-C chains.',
                                delivers: ['Failure Net', 'Complete DFMEA Rows']
                            },
                            {
                                id: 'step5', num: 5, title: 'Risk Analysis', icon: <BarChart2 size={20} />, color: 'amber',
                                apps: ['Risk Matrix'],
                                description: 'Assign Severity, Occurrence, Detection ratings. Determine Action Priority.',
                                delivers: ['S-O-D Ratings', 'Risk Prioritization']
                            },
                            {
                                id: 'step6', num: 6, title: 'Optimization', icon: <CheckCircle size={20} />, color: 'cyan',
                                apps: ['Action Tracker'],
                                description: 'Plan and track actions to reduce risk. Re-evaluate after implementation.',
                                delivers: ['Action Plan', 'Revised Ratings']
                            },
                            {
                                id: 'step7', num: 7, title: 'Results Documentation', icon: <ClipboardCheck size={20} />, color: 'pink',
                                apps: ['DVP&R', 'Reports'],
                                description: 'Document results, update DVP&R, and communicate risks to management.',
                                delivers: ['DVP&R Plan/Report', 'Executive Summary']
                            },
                        ].map(step => (
                            <div
                                id={step.id}
                                key={step.id}
                                className={`
                                    p-6 rounded-xl border-l-4
                                    ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}
                                `}
                                style={{ borderLeftColor: step.color === 'blue' ? '#3B82F6' : step.color === 'red' ? '#EF4444' : step.color === 'green' ? '#10B981' : step.color === 'purple' ? '#8B5CF6' : step.color === 'amber' ? '#F59E0B' : step.color === 'cyan' ? '#06B6D4' : '#EC4899' }}
                            >
                                <div className="flex items-center gap-4 mb-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white font-bold">
                                        {step.num}
                                    </div>
                                    <div>
                                        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                            Step {step.num}: {step.title}
                                        </h3>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                            {step.apps.map(app => (
                                                <span key={app} className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                                    {app}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className={`mb-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{step.description}</p>
                                <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                    <div className="text-sm font-medium text-slate-500 mb-1">Delivers:</div>
                                    <div className="flex flex-wrap gap-2">
                                        {step.delivers.map(item => (
                                            <span key={item} className="text-xs bg-primary/20 text-primary-700 dark:text-primary-300 px-2 py-1 rounded">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Footer */}
                <div className={`p-6 rounded-xl border text-center ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                    <Award className="mx-auto text-primary mb-3" size={32} />
                    <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        Ready to Explore?
                    </h3>
                    <p className="text-slate-500 mb-4">
                        Navigate to the Dashboard to select a project and start your analysis.
                    </p>
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-purple-500 text-white rounded-xl font-medium hover:opacity-90"
                    >
                        <Zap size={18} />
                        Go to Dashboard
                    </Link>
                </div>
            </main>
        </div>
    );
};

export default UserGuidePage;
